import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { isFramlPSource } from '../../../../util/framlP';
import { Switch } from 'antd';
import {
  ActionItem,
  ActionLabel,
  Dropdown,
  SearchRow,
  MainFiltersRow,
  DownloadButtonWrapper,
  Tooltip
} from '../../../common/TransactionDetalis/styled';
import RadioGroup from '../../../../components/RadioGroup';
import RangeDatePicker from '../../../../components/RangeDatePicker';
import TransactionSearchBar from '../../../common/TransactionDetalis/TransactionSearchBar';
import AdvancedFiltersPanel from '../../../common/TransactionDetalis/AdvancedFiltersPanel';
import SortOrderToggle from '../../../common/TransactionDetalis/SortOrderToggle';
import DownloadDropdown from '../../../common/TransactionDetalis/DownloadDropdown';
import { NetworkLayer } from '../../../../services/tmCommon';
import { convertDateInputToEpoch } from '../../../../util/timezone';

import moment from 'moment';

const DURATION_ITEMS = [
  { label: '7 Days', value: 7 },
  { label: '30 Days', value: 30 },
  { label: '90 Days', value: 90 },
  { label: '180 Days', value: 180 },
  { label: '360 Days', value: 360 }
];

// FIN-11648 / B3 — Activity-tab search facets.
const ACTIVITY_SEARCH_FIELDS = [
  { value: 'activityId', label: 'Activity Id' },
  { value: 'partyKey', label: 'Party Key' },
  { value: 'ipAddress', label: 'IP Address' }
];

// FIN-11542 — Activity-tab advanced filter facet keys + labels.
// Option lists for each facet are fetched from BE
// (`amlsTMCommonActivityFilterOptionsAPI`) on mount; the structure below only
// pins the field metadata (key + label + fetched-options-source-key) so the
// AdvancedFiltersPanel can render the same shape it always did.
const ACTIVITY_FILTER_FIELD_DEFS = [
  { key: 'activityType', label: 'Activity Type', optionsKey: 'activityTypes' },
  {
    key: 'activityStatus',
    label: 'Activity Status',
    optionsKey: 'activityStatuses'
  },
  { key: 'channel', label: 'Channel', optionsKey: 'channels' }
];

const EMPTY_OPTIONS = {
  activityTypes: [],
  activityStatuses: [],
  channels: []
};

// FIN-12067 — alphabetically sort a facet option list (locale-aware). Options
// may be plain strings or { value, label } objects; sort by the visible label
// (falling back to value) without mutating the source array. The option VALUE
// is never transformed (FIN-12068 — it must reach the BE byte-identical).
const optionSortKey = (opt) =>
  String((opt && (opt.label ?? opt.value)) ?? opt ?? '');

const sortOptions = (options) =>
  [...options].sort((a, b) => optionSortKey(a).localeCompare(optionSortKey(b)));

const ActivityInfoFilters = ({
  hasTriggeredOnlySwitch,
  hasTypologiesFilter,
  typologies = [],
  showLabels,
  filters,
  onFilterChange,
  // FIN-11542 Phase-C Defect-2/3/4 — dedicated functional-merge handlers from
  // the host so search / advanced-filter / sort changes don't spread a stale
  // `filters` closure (the search debounce captures the initial query). Fall
  // back to onFilterChange for callers that don't supply them.
  onSearchChange,
  onAdvancedFiltersChange,
  onSortChange: onSortChangeProp,
  alertDateTO,
  isPrevention,
  // FIN-11542 — FRAML-P source ID for variant facet gating
  alertSourceId,
  // FIN-12168 — alert scope so the BE returns the DISTINCT facet values present
  // in this alert's activities (incl. Channel from custom_data) instead of the
  // static canonical lists. customerId may be a FRAML-P party-key JSON blob.
  alertId,
  customerId,
  variantOptions,
  // FIN-11542 — mock parity with Tx tab: a 2-option Download dropdown next to
  // the Sort dropdown ("Download Current Page" / "Download All Matching").
  onDownloadCurrentPage,
  onDownloadAll,
  isDownloading,
  isDownloadDisabled,
  currentPageCount,
  totalCount
}) => {
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);

  // FIN-11542 — BE-served Advanced Filter option lists. Starts empty so the
  // picker mounts cleanly while the fetch is in flight; on rejection we keep
  // EMPTY_OPTIONS so the picker stays mounted-but-empty rather than crashing.
  const [filterOptions, setFilterOptions] = useState(EMPTY_OPTIONS);

  useEffect(() => {
    const networkLayer = new NetworkLayer();
    let cancelled = false;
    // FIN-12168 — scope the distinct-facet fetch to the LIVE Triggered Only toggle
    // + the current date window so the dropdowns RE-SCOPE when the toggle flips
    // (ON = triggered join; OFF = full party set minus triggered) and match the
    // listing window. Without triggeredOnly/from-to the fetch fired once on mount,
    // froze on the triggered scope, and returned ALL-BLANK facets for API-ingested
    // alerts with zero triggered_activity rows. Mirror the listing's day-boundary
    // epoch conversion; clone the moment so we don't mutate shared filter state.
    const fromEpoch = filters?.from
      ? convertDateInputToEpoch(
          moment(filters.from)
            .startOf('day')
            .format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
          true
        )
      : undefined;
    const toEpoch = filters?.to
      ? convertDateInputToEpoch(
          moment(filters.to).endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
          true
        )
      : undefined;
    networkLayer
      // FIN-12168 — pass the alert scope so the BE returns the DISTINCT facet
      // values present in this alert's activities (Channel from custom_data).
      // Absent alertId (non-alert contexts) → BE serves the static canonical lists.
      .amlsTMCommonActivityFilterOptionsAPI({
        alertId,
        customerId,
        isTriggeredOnly: filters?.isTriggeredOnly,
        from: fromEpoch,
        to: toEpoch,
        // FIN-12168 — forward the selected typology/variant so the dropdowns show
        // only the distinct values present in the (typology/variant-)filtered
        // results, matching the listing scope. Re-scopes when they change (deps).
        typologyId: filters?.typologyId,
        variantId: filters?.variantId
      })
      .then((response) => {
        if (cancelled || !response) return;
        // Defensive: only adopt fields we expect; tolerate partial responses.
        // FIN-12067 — sort each facet option list alphabetically (locale-aware)
        // so the Advanced Filter dropdowns (Activity Type / Status / Channel)
        // render in a predictable order. Values are kept VERBATIM (FIN-12068 —
        // no case transform); only the list ORDER changes.
        setFilterOptions({
          activityTypes: Array.isArray(response.activityTypes)
            ? sortOptions(response.activityTypes)
            : [],
          activityStatuses: Array.isArray(response.activityStatuses)
            ? sortOptions(response.activityStatuses)
            : [],
          channels: Array.isArray(response.channels)
            ? sortOptions(response.channels)
            : []
        });
      })
      .catch(() => {
        if (cancelled) return;
        setFilterOptions(EMPTY_OPTIONS);
      });
    return () => {
      cancelled = true;
    };
    // FIN-12168 — refire on Triggered Only toggle + window change so the facets
    // re-scope live (onChangeTriggeredOnly mutates filters.isTriggeredOnly).
  }, [
    alertId,
    customerId,
    filters?.isTriggeredOnly,
    filters?.from,
    filters?.to,
    // FIN-12168 — refetch when typology/variant changes so facets re-scope.
    filters?.typologyId,
    filters?.variantId
  ]);

  // Compose the Advanced Filter facet defs with the fetched option lists.
  // FIN-11542 — append a 4th "Variants" facet for FRAML-P alerts (24/25/29 only).
  const activityFilterFields = useMemo(() => {
    const base = ACTIVITY_FILTER_FIELD_DEFS.map(
      ({ key, label, optionsKey }) => ({
        key,
        label,
        options: filterOptions[optionsKey] || []
      })
    );
    if (isFramlPSource(alertSourceId)) {
      base.push({
        key: 'variantId',
        label: 'Variants',
        // FIN-12067 — sort the Variants options alphabetically too (label).
        options: sortOptions(
          (variantOptions || []).map((v) => ({
            value: typeof v === 'string' ? v : v.id || v.value,
            label: typeof v === 'string' ? v : v.name || v.label
          }))
        )
      });
    }
    return base;
  }, [filterOptions, alertSourceId, variantOptions]);

  // FIN-11305 (P4): Triggered Only switch on Activity tab — parallel to TM
  // Transactions tab. When OFF, typology selection resets to 'all' (mirrors
  // TransactionDetailsFilters behaviour).
  const onChangeTriggeredOnly = (value) => {
    onFilterChange({
      ...filters,
      isTriggeredOnly: value,
      typologyId: value ? filters?.typologyId : 'all'
    });
  };

  const typologiesOptions = useMemo(
    () => [
      {
        id: 'all',
        name: 'All'
      },
      ...typologies?.map(({ id, name }) => ({
        id,
        name
      }))
    ],
    [typologies]
  );

  const onRadioGroupChange = (days) => {
    const from = moment(alertDateTO).subtract(days - 1, 'days');

    onFilterChange({
      ...filters,
      duration: days,
      from,
      to: alertDateTO
    });
  };

  const onDatePickerChange = (range) => {
    onFilterChange({
      ...filters,
      duration: null,
      from: range[0],
      to: range[1]
    });
  };

  const disableDate = (current) => {
    return current > alertDateTO.endOf('day');
  };

  const onTypologyChange = (typology) => {
    onFilterChange({
      ...filters,
      typologyId: typology.id
    });
  };

  const onSearch = ({ searchField, searchValue }) => {
    const payload = {
      searchField: searchField || undefined,
      searchTerm: searchValue || undefined
    };
    if (typeof onSearchChange === 'function') {
      onSearchChange(payload);
    } else {
      onFilterChange({ ...filters, ...payload });
    }
  };

  const onAdvancedFiltersApply = ({ advancedFilters }) => {
    const value = advancedFilters || {};
    if (typeof onAdvancedFiltersChange === 'function') {
      onAdvancedFiltersChange(value);
    } else {
      onFilterChange({ ...filters, advancedFilters: value });
    }
  };

  const onSortChange = (sortOrder) => {
    if (typeof onSortChangeProp === 'function') {
      onSortChangeProp(sortOrder);
    } else {
      onFilterChange({ ...filters, sortOrder });
    }
  };

  // FIN-11542 — Layout parity with the Transaction Information tab
  // (TransactionDetails.js). The Tx tab splits its controls into two stacked
  // rows — a full-width search row (search bar + Advanced Filters), then a
  // bottom-aligned filter row (Duration pills + Date Range + Sort + Download).
  // Activity previously crammed search + Triggered + Duration + Date Range +
  // Typology + Sort + Download into a single non-wrapping flex Container, so the
  // Duration RadioGroup overflowed and stacked vertically and the Date Range
  // inputs collapsed. Adopting the same SearchRow / MainFiltersRow structure
  // (MainFiltersRow already wraps via flex-wrap) fixes both. JSX control order
  // is unchanged so the combobox-index assertions in the tests still hold
  // (search-by [0], sort [1], drawer facets [2..4]).
  return (
    <>
      <SearchRow>
        <TransactionSearchBar
          onSearch={onSearch}
          searchFields={ACTIVITY_SEARCH_FIELDS}
          advancedFiltersOpen={advancedFiltersOpen}
          onToggleAdvancedFilters={setAdvancedFiltersOpen}
          advancedFiltersOverlay={
            <AdvancedFiltersPanel
              filters={filters || {}}
              filterFields={activityFilterFields}
              onApply={(values) => {
                onAdvancedFiltersApply(values);
                setAdvancedFiltersOpen(false);
              }}
              onClose={() => setAdvancedFiltersOpen(false)}
            />
          }
        />
      </SearchRow>
      <MainFiltersRow>
        {hasTriggeredOnlySwitch && (
          // FIN-11542 fix: label beside the toggle (inline), per mock — matches
          // the Transaction tab; not stacked above like Duration / Date Range.
          <ActionItem style={{ display: 'flex', alignItems: 'center' }}>
            {showLabels && (
              <ActionLabel style={{ marginBottom: 0, marginRight: 8 }}>
                Triggered Only
              </ActionLabel>
            )}
            <Switch
              aria-label="Triggered Only"
              checked={filters?.isTriggeredOnly}
              onChange={onChangeTriggeredOnly}
            />
          </ActionItem>
        )}
        <ActionItem>
          {showLabels && <ActionLabel>Duration</ActionLabel>}
          <RadioGroup
            items={DURATION_ITEMS}
            defaultValue={filters?.duration}
            onChange={onRadioGroupChange}
            size="small"
          />
        </ActionItem>
        <ActionItem>
          {showLabels && <ActionLabel>Date Range</ActionLabel>}
          <RangeDatePicker
            defaultDates={[filters?.from, filters?.to]}
            onChange={onDatePickerChange}
            size="small"
            disabledDate={disableDate}
          />
        </ActionItem>
        {/* FIN-11542 D3 — the Typology/Variant dropdown is a TM-only concept
            (ActivityInformation is shared between TM and FRAML-P). The mock's
            FRAML-P Activity strip has NO Variant filter, and its presence
            squeezed the Duration RadioGroup into a 2-row wrap (D2). Gate it OFF
            for FRAML-P (isPrevention) while keeping it for TM. */}
        {hasTypologiesFilter && !isPrevention && (
          <ActionItem>
            {showLabels && (
              <ActionLabel>
                {isPrevention ? 'Variant' : 'Typologies'}
              </ActionLabel>
            )}
            <Dropdown
              dropdownValue={typologiesOptions.find(
                ({ id }) => id === filters?.typologyId
              )}
              defaultValue={`all`}
              dropdownOptions={typologiesOptions?.map((option) => ({
                ...option,
                name: <Tooltip content={option.name}>{option.name}</Tooltip>
              }))}
              handleValueChange={onTypologyChange}
            />
          </ActionItem>
        )}
        <SortOrderToggle
          label="Sort Activity Date:"
          value={filters?.sortOrder || 'DESC'}
          onChange={onSortChange}
        />
        {typeof onDownloadCurrentPage === 'function' &&
          typeof onDownloadAll === 'function' && (
            <DownloadButtonWrapper>
              <DownloadDropdown
                onDownloadCurrentPage={onDownloadCurrentPage}
                onDownloadAll={onDownloadAll}
                isDownloading={isDownloading}
                isDisabled={isDownloadDisabled}
                currentPageCount={currentPageCount}
                totalCount={totalCount}
              />
            </DownloadButtonWrapper>
          )}
      </MainFiltersRow>
    </>
  );
};

ActivityInfoFilters.defaultProps = {
  hasTriggeredOnlySwitch: false,
  hasTypologiesFilter: true,
  showLabels: true,
  filters: []
};

ActivityInfoFilters.propTypes = {
  hasTriggeredOnlySwitch: PropTypes.bool,
  hasTypologiesFilter: PropTypes.bool,
  typologies: PropTypes.array,
  showLabels: PropTypes.bool,
  filters: PropTypes.object,
  onFilterChange: PropTypes.func.isRequired,
  onSearchChange: PropTypes.func,
  onAdvancedFiltersChange: PropTypes.func,
  onSortChange: PropTypes.func,
  alertDateTO: PropTypes.object,
  isPrevention: PropTypes.bool,
  alertSourceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  alertId: PropTypes.string,
  customerId: PropTypes.string,
  variantOptions: PropTypes.array,
  onDownloadCurrentPage: PropTypes.func,
  onDownloadAll: PropTypes.func,
  isDownloading: PropTypes.bool,
  isDownloadDisabled: PropTypes.bool,
  currentPageCount: PropTypes.number,
  totalCount: PropTypes.number
};

export default ActivityInfoFilters;
